"use client";

import { useState, useTransition } from "react";
import { Send, CheckCircle, User, Mail, Phone, MessageCircle, AlertCircle, FileText } from "lucide-react";
import { submitContactAction } from "@/actions/tenant/tenant-contact";

interface ContactFormProps {
    siteId: string;
}

export default function ContactForm({ siteId }: ContactFormProps) {
    const [isPending, startTransition] = useTransition();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState("");

    // 'subject' alanı şemana uygun olarak eklendi
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError("");
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        startTransition(async () => {
            const payload = {
                siteId,
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                subject: formData.subject,
                message: formData.message
            };

            const result = await submitContactAction(payload);

            if (result.success) {
                setIsSubmitted(true);
                // Formu temizle
                setFormData({ name: "", email: "", phone: "", subject: "", message: "" });

                // Başarı mesajını 5 saniye sonra kaldır
                setTimeout(() => setIsSubmitted(false), 5000);
            } else {
                setError(result.error || "Mesajınız gönderilemedi. Lütfen daha sonra tekrar deneyin.");
            }
        });
    };

    return (
        <div className="bg-white rounded-4xl shadow-sm border border-slate-100 p-8 md:p-10">
            <div className="flex items-center mb-8">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary), transparent 90%)', color: 'var(--color-primary)' }}>
                    <Send className="h-6 w-6" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Mesaj Gönderin</h2>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-start">
                    <AlertCircle className="h-5 w-5 text-rose-500 mr-3 mt-0.5 shrink-0" />
                    <div>
                        <p className="text-rose-800 font-bold text-sm">Hata</p>
                        <p className="text-rose-600 text-sm mt-0.5">{error}</p>
                    </div>
                </div>
            )}

            {isSubmitted ? (
                <div className="text-center py-16 animate-in fade-in zoom-in duration-500">
                    <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="h-12 w-12 text-emerald-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">Mesajınız İletildi!</h3>
                    <p className="text-slate-600 mb-6 text-lg max-w-sm mx-auto">Uzman ekibimiz talebinizi aldı. En kısa sürede sizinle iletişime geçeceğiz.</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in duration-500">
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="text-sm font-bold text-slate-700 mb-2 flex items-center">
                                <User className="h-4 w-4 mr-1.5 opacity-50" /> Adınız Soyadınız
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-5 py-4 bg-slate-50 border border-slate-200 text-slate-800 rounded-2xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                                placeholder="Örn: Ahmet Yılmaz"
                                required
                                disabled={isPending}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="text-sm font-bold text-slate-700 mb-2 flex items-center">
                                <Phone className="h-4 w-4 mr-1.5 opacity-50" /> Telefon Numaranız
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-5 py-4 bg-slate-50 border border-slate-200 text-slate-800 rounded-2xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                                placeholder="05XX XXX XX XX"
                                disabled={isPending}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-bold text-slate-700 mb-2 flex items-center">
                                <Mail className="h-4 w-4 mr-1.5 opacity-50" /> E-posta Adresiniz
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-5 py-4 bg-slate-50 border border-slate-200 text-slate-800 rounded-2xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                                placeholder="ornek@email.com"
                                required
                                disabled={isPending}
                            />
                        </div>
                    </div>

                    {/* YENİ EKLENEN KONU (SUBJECT) ALANI */}
                    <div>
                        <label className="text-sm font-bold text-slate-700 mb-2 flex items-center">
                            <FileText className="h-4 w-4 mr-1.5 opacity-50" /> İletişim Konusu
                        </label>
                        <select
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            className="w-full px-5 py-4 bg-slate-50 border border-slate-200 text-slate-800 rounded-2xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium appearance-none"
                            required
                            disabled={isPending}
                        >
                            <option value="" disabled>Lütfen bir konu seçin</option>
                            <option value="Bilgi Almak İstiyorum">Bilgi Almak İstiyorum</option>
                            <option value="Poliçe Yenileme">Poliçe Yenileme</option>
                            <option value="Hasar Bildirimi">Hasar Bildirimi</option>
                            <option value="Teklif İsteği">Teklif İsteği</option>
                            <option value="Diğer">Diğer</option>
                        </select>
                    </div>

                    <div>
                        <label className="text-sm font-bold text-slate-700 mb-2 flex items-center">
                            <MessageCircle className="h-4 w-4 mr-1.5 opacity-50" /> Mesajınız veya Talebiniz
                        </label>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={5}
                            className="w-full px-5 py-4 bg-slate-50 border border-slate-200 text-slate-800 rounded-2xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium resize-none"
                            placeholder="Mesaj detaylarınızı buraya yazabilirsiniz..."
                            required
                            disabled={isPending}
                        ></textarea>
                    </div>

                    <button
                        type="submit"
                        disabled={isPending}
                        className="w-full text-white py-4 px-6 rounded-2xl font-bold shadow-lg hover:-translate-y-1 transition-all duration-300 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed group text-lg"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                    >
                        {isPending ? (
                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                Mesajı Gönder <Send className="h-5 w-5 ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                            </>
                        )}
                    </button>

                    <p className="text-xs text-slate-400 font-medium text-center">
                        Bilgileriniz KVKK kapsamında korunmakta olup üçüncü şahıslarla paylaşılmaz.
                    </p>
                </form>
            )}
        </div>
    );
}